import {
  type DataGridRef,
  type DialogRef,
  DynamicForm,
  type DynamicFormButtonSetting,
  type DynamicFormRef,
  type JsonDataItem,
  type JsonDataWrapper,
  type OdinDataRetrieval,
  type OdinDataRetrievalOptions,
  OdinDataSender,
} from '@myosh/odin-components';
import { debounce } from 'lodash';
import { type Ref, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BehaviorSubject, Subject, skip, takeUntil } from 'rxjs';
import { v4 } from 'uuid';
import type { FormChangedFields } from '../../@types/common';
import type { ExtendedDynamicFormSettings } from '../../@types/forms';
import type { OccupationItem, OccupationMutationResponse } from '../../@types/occupations';
import type { RecordNotification } from '../../@types/record-notifications';
import useDynamicFormNotifier from '../../hooks/use-dynamic-form-notifier';
import usePerformingSaveToast from '../../hooks/use-performing-save-toast';
import { useUserAccess } from '../../hooks/use-user-access';
import { saveButton } from '../../pages/admin/admin-utils';
import { useGetNotificationByIdQuery } from '../../redux/services/notification';
import { getDataGridReference } from '../../services/data-grid.service';
import { showError, showSuccess } from '../../services/notification.service';
import { getChangedFieldsData } from '../../utils/common-administration-utils';
import { forceAssert } from '../../utils/type-guards';
import { RecordNotificationsPermissions } from '../../utils/user.permissions';
import type { ActiveRecordContextProps } from '../active-record/active-record.component';
import CloseActiveItemButton from '../common/close-active-item-button';
import ConfirmationDialog from '../common/confirmation-dialog.component';
import { FormLoading } from '../form/form-loading.component';
import { FormTitle } from '../form/form-title.component';
import { getNotificationFormSettings } from './notification-form-settings';
import { createUINotitificationData, getNotificationsFieldOptions } from './notifications-utils';

export interface NotificationDetailsProps {
  notificationId: number;
  title: string;
  editable?: boolean;
  isNewNotification?: boolean;
  onClose: (id: string) => void;
  panelSettings?: BehaviorSubject<ActiveRecordContextProps>;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;
}
interface NotificationDetailsRef {
  submitForm?: () => void;
}

const NotificationDetails = (props: NotificationDetailsProps, ref: Ref<NotificationDetailsRef>) => {
  const {
    notificationId,
    title,
    editable,
    isNewNotification = false,
    onClose,
    panelSettings,
    onCloseActiveRecordConfirmationModalRef,
  } = props;

  const [notification, setNotification] = useState<RecordNotification>();
  const [panelContextProps, setPanelContextProps] = useState<ActiveRecordContextProps>();
  const [formButtons, setFormButtons] = useState<Array<DynamicFormButtonSetting>>();

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const saveModalReference = useRef<DialogRef>(null);
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);
  const destroySubject = useRef(new Subject<void>());
  const dynamicFormId = useRef(v4());
  const closeOnModalSaveRef = useRef(false);
  const formSettings = useRef<ExtendedDynamicFormSettings>(getNotificationFormSettings());

  const { readOnly, setReadOnly, editAccess } = useUserAccess({
    initialAccess: isNewNotification || Boolean(notificationId) === editable,
    permissions: {
      MODIFY: [
        RecordNotificationsPermissions.NOTIFICATIONS_MODIFY,
        RecordNotificationsPermissions.NOTIFICATIONS_CREATE,
      ],
    },
  });
  const { t } = useTranslation();

  const { data, isLoading, isFetching } = useGetNotificationByIdQuery(notificationId, {
    skip: notificationId === undefined,
  });

  const { notifyDirty, notifySaveSucceeded, notifySaveFailed } = useDynamicFormNotifier(dynamicFormId.current);

  const { hidePerformingSaveToast } = usePerformingSaveToast({
    shouldFreezeActiveRecord: true,
    disabled: panelContextProps?.batchSaveInProgress,
  });

  useEffect(() => {
    if (data && !isFetching) {
      setNotification(createUINotitificationData(data));
      const newFormButtons: Array<DynamicFormButtonSetting> = [saveButton];
      if (readOnly) {
        setFormButtons(newFormButtons.map((button) => ({ ...button, disabled: true })));
      } else {
        setFormButtons(newFormButtons);
      }
    }
  }, [data, isFetching, readOnly]);

  useEffect(() => {
    panelSettings?.pipe(skip(1), takeUntil(destroySubject.current)).subscribe((settings) => {
      setPanelContextProps(settings);
    });

    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (result.buttonName === 'save' && dynamicFormReference.current?.formIsDirty()) {
          dynamicFormReference.current?.submitForm(true);
        } else if (result.buttonName === 'discard') {
          onClose(dynamicFormId.current);
        }
      });

    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    submitForm: dynamicFormReference.current?.submitForm,
  }));

  useEffect(() => {
    if (notification?.archived) {
      setReadOnly(true);
    }
  }, [notification]);

  const showSaveToast = () => {
    if (!panelContextProps?.batchSaveInProgress) {
      showSuccess(t('record-saved'));
    }
    notifySaveSucceeded(dynamicFormId.current);
    closeOnModalSaveRef.current = true;
    if (!notificationId || closeOnModalSaveRef.current) {
      onClose(dynamicFormId.current);
    }
  };

  const showErrorNotificationToast = () => {
    showError(t('occupation-details-save-failed'));
    notifySaveFailed(dynamicFormId.current);
  };

  const createNotification = useCallback((data: JsonDataItem) => {
    saveNotificationData(data, (response) => {
      const convertedResponse = forceAssert<OccupationMutationResponse>(response);
      if ('data' in convertedResponse && convertedResponse.data?.result) {
        showSaveToast();
        refreshGrid();
      } else if ('error' in convertedResponse) {
        hidePerformingSaveToast();
        showErrorNotificationToast();
      }
    });
  }, []);

  const onFormSubmit = (data: JsonDataItem) => {
    if (notificationId && notification) {
      const dirtyFields = dynamicFormReference.current?.formDirtyFields() || {};
      const patchData = getChangedFieldsData(forceAssert<FormChangedFields<OccupationItem>>(dirtyFields), data);
      saveNotificationData({ id: notificationId, ...patchData }, (response) => {
        const convertedResponse = forceAssert<OccupationMutationResponse>(response);
        if ('data' in convertedResponse && convertedResponse.data?.result) {
          dataGridRef.current?.api.data.recordChanged(notificationId);
          hidePerformingSaveToast();
          showSaveToast();
        } else if ('error' in convertedResponse) {
          hidePerformingSaveToast();
          showErrorNotificationToast();
        }
      });
    } else {
      createNotification(data);
    }
  };

  const refreshGrid = () => {
    dataGridRef.current?.api.data.getDataOverwritePageCache();
  };

  const saveNotificationData = (
    notificationData: Partial<OccupationItem>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    successCallback: (response?: unknown) => void
  ) => {
    //showPerformingSaveToast();
    if (notificationData.id) {
      //TODO implement update notification
    } else {
      //TODO implement add notification
    }
  };

  const onSaveConfirm = () => {
    const data = dynamicFormReference.current?.getData() || {};
    onFormSubmit(data);
  };

  const onSaveCancel = () => onClose(dynamicFormId.current);

  const onCloseNotificationDetails = () => {
    if (!dynamicFormReference.current?.formIsDirty()) {
      onClose(dynamicFormId.current);
    } else {
      saveModalReference.current?.show();
    }
  };

  const dataRetrieval: OdinDataRetrieval = {
    getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if ('COMBOBOX' === options?.fieldType || 'OPTIONGROUP' === options?.fieldType) {
        await getNotificationsFieldOptions(subscriber, options);
      } else {
        subscriber.sendData();
      }
    },
  };

  const onFieldChanged = debounce(() => notifyDirty(dynamicFormReference.current), 500);

  return (
    <>
      <div className="flex flex-shrink-0 flex-col">
        <div className="flex">
          <FormTitle title={title} />
          {!isLoading && (
            <div className="flex min-w-min justify-end">
              <CloseActiveItemButton onClick={onCloseNotificationDetails} />
            </div>
          )}
        </div>
      </div>
      <div className="admin-form-default">
        {!formSettings.current && <FormLoading />}
        {formSettings.current && (
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={notification}
            settings={formSettings.current}
            dataRetrieval={dataRetrieval}
            buttons={formButtons}
            buttonsLocation={0}
            readOnly={!editAccess}
            buttonsPosition={1}
            onSubmit={onFormSubmit}
            onFieldChanged={onFieldChanged}
          />
        )}
      </div>
      <ConfirmationDialog
        ref={saveModalReference}
        onConfirm={onSaveConfirm}
        onClose={onSaveCancel}
        cancelButtonText={t('discard')}
        hasCancelButton
      />
    </>
  );
};
export default forwardRef(NotificationDetails);
